import { useState, useEffect } from 'react'
import { MdFlight, MdSchedule, MdAdd } from 'react-icons/md'
import { useDispatch } from 'react-redux'
import FlightList from '../../components/flights/FlightList'
import { Button, LoadingSpinner, SearchBar, FilterBar } from '../../components/ui'
import { useFlight } from '../../hooks/useFlight'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function FlightsPage() {
  const dispatch = useDispatch()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const [flights, setFlights] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filter, setFilter] = useState('all') // all, scheduled, in-flight, delayed
  const [stats, setStats] = useState({
    scheduled: 0,
    inFlight: 0,
    delayed: 0,
  })

  const { getFlightsAsync } = useFlight()

  useEffect(() => {
    const channelName = getWebsocketChannel().flightList

    loadFlights()

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadFlights = async () => {
    try {
      setIsLoading(true)
      const flightsData = await getFlightsAsync()
      setFlights(flightsData || [])

      // Calculate stats
      const statsData = {
        scheduled: 0,
        inFlight: 0,
        delayed: 0,
      }

      flightsData?.forEach((flight) => {
        switch (flight.status?.toLowerCase()) {
          case 'scheduled':
            statsData.scheduled++
            break
          case 'in flight':
          case 'inflight':
            statsData.inFlight++
            break
          case 'delayed':
            statsData.delayed++
            break
          default:
            statsData.scheduled++
        }
      })

      setStats(statsData)
    } catch (error) {
      console.error('Error loading flights:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getFilteredFlights = () => {
    let filteredFlights = flights

    if (filter === 'scheduled') {
      filteredFlights = flights.filter((flight) => flight.status?.toLowerCase() === 'scheduled')
    } else if (filter === 'in-flight') {
      filteredFlights = flights.filter(
        (flight) =>
          flight.status?.toLowerCase() === 'in flight' ||
          flight.status?.toLowerCase() === 'inflight',
      )
    } else if (filter === 'delayed') {
      filteredFlights = flights.filter((flight) => flight.status?.toLowerCase() === 'delayed')
    }

    if (searchTerm) {
      filteredFlights = filteredFlights.filter(
        (flight) =>
          flight.flightNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flight.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flight.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flight.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          flight.pilot?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    }

    return filteredFlights
  }

  const filteredFlights = getFilteredFlights()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Flight Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage all flight operations
            </p>
          </div>

          <Button
            variant="primary"
            onClick={() => {
              /* TODO: Implement create flight */
            }}
            className="flex items-center space-x-2"
          >
            <MdAdd className="text-lg" />
            <span>Add Flight</span>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Scheduled Flights */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Scheduled</h3>
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <MdSchedule className="text-primary-600 dark:text-primary-400 text-xl" />
            </div>
          </div>
          <p className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            {stats.scheduled}
          </p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Today's flights</p>
        </div>

        {/* In Flight */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">In Flight</h3>
            <div className="w-10 h-10 bg-success-100 dark:bg-success-900 rounded-lg flex items-center justify-center">
              <MdFlight className="text-success-600 dark:text-success-400 text-xl" />
            </div>
          </div>
          <p className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            {stats.inFlight}
          </p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Currently airborne</p>
        </div>

        {/* Delayed */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Delayed</h3>
            <div className="w-10 h-10 bg-warning-100 dark:bg-warning-900 rounded-lg flex items-center justify-center">
              <MdFlight className="text-warning-600 dark:text-warning-400 text-xl" />
            </div>
          </div>
          <p className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
            {stats.delayed}
          </p>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">Delayed flights</p>
        </div>
      </div>

      {/* Flight List */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">All Flights</h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search flights..."
            />

            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={filter}
                onFilterChange={(e) => setFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Flights' },
                  { value: 'scheduled', label: `Scheduled (${stats.scheduled})` },
                  { value: 'in-flight', label: `In Flight (${stats.inFlight})` },
                  { value: 'delayed', label: `Delayed (${stats.delayed})` },
                ]}
              />

              <Button
                variant="ghost"
                onClick={loadFlights}
                disabled={isLoading}
                className="flex items-center space-x-2"
              >
                <MdSchedule className={isLoading ? 'animate-spin' : ''} />
                <span>Refresh</span>
              </Button>
            </div>
          </div>
        </div>

        <FlightList flights={filteredFlights} />
      </div>
    </div>
  )
}

export default FlightsPage
